import { useState } from "react";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { ItemList } from "@/shared/components/ui/ItemList";
import { ListSearch } from "@/shared/components/ui/ListSearch";
import { Section } from "@/shared/components/ui/Section";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { Tabs } from "@/shared/components/ui/Tabs";
import { useServices } from "../hooks/serviceQueries";
import type { Service } from "../types/serviceTypes";
import { ServiceRow } from "./ServiceRow";
import { ServiceReorder } from "./ServiceReorder";

type ServiceStatus = "all" | "active" | "inactive";

interface ServicesListProps {
  onServiceClick: (service: Service) => void;
  onAddService?: () => void;
  selectedServices?: Service[];
  mode?: "catalogue" | "picker";
  existingSelections?: Service[];
  search?: string;
  onSearchChange?: (search: string) => void;
  status?: ServiceStatus;
  onStatusChange?: (status: ServiceStatus) => void;
  onClearFilters?: () => void;
}

export function ServicesList({
  onServiceClick, selectedServices, mode = "catalogue", existingSelections = [],
  search, onSearchChange, status, onStatusChange, onClearFilters, onAddService,
}: ServicesListProps) {
  const { data: services = [], isLoading, error, refetch } = useServices();
  const { businessId } = useCurrentBusiness();
  const [reorderBusinessId, setReorderBusinessId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [localSearch, setLocalSearch] = useState("");
  const [localStatus, setLocalStatus] = useState<ServiceStatus>("all");
  const searchText = search ?? localSearch;
  const filter = status ?? localStatus;
  const setSearch = onSearchChange ?? setLocalSearch;
  const setStatus = onStatusChange ?? setLocalStatus;
  const query = searchText.trim().toLowerCase();
  const selectable = mode === "picker"
    ? [
      ...services.filter((service) => service.isActive || existingSelections.some((item) => item.id === service.id))
        .map((service) => existingSelections.find((item) => item.id === service.id) ?? service),
      ...existingSelections.filter((item) => !services.some((service) => service.id === item.id)),
    ]
    : services;
  const matches = selectable.filter((service) =>
    (mode === "picker" || filter === "all" || service.isActive === (filter === "active")) &&
    `${service.name} ${service.description ?? ""}`.toLowerCase().includes(query),
  );
  const title = mode === "picker" ? "Available services"
    : filter === "active" ? "Active services" : filter === "inactive" ? "Inactive services" : "All services";
  const filtered = !!query || (mode === "catalogue" && filter !== "all");
  const clearFilters = () => {
    if (onClearFilters) onClearFilters();
    else { setSearch(""); setStatus("all"); }
  };

  return (
    <div className="space-y-[26px]">
      <ListSearch label="Search services" placeholder="Name or description" value={searchText} onChange={setSearch} />
      {mode === "catalogue" && (
        <Tabs
          label="Service status"
          value={filter}
          onValueChange={setStatus}
          options={[{ value: "all", label: "All" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]}
        />
      )}
      <Section
        title={title}
        count={!isLoading && !error ? `${matches.length} ${matches.length === 1 ? "service" : "services"}` : undefined}
        action={(filtered || mode === "catalogue") && <div className="flex flex-wrap justify-end gap-1">
          {filtered && <SectionAction onClick={clearFilters}>Clear {mode === "picker" ? "search" : "filters"}</SectionAction>}
          {mode === "catalogue" && <SectionAction
            disabled={isLoading || !!error || services.length < 2 || !businessId}
            onClick={() => { setFeedback(""); setReorderBusinessId(businessId ?? null); }}
          >Reorder</SectionAction>}
        </div>}
      >
        {error ? <ErrorState message={error.message} onRetry={() => void refetch()} /> : (
          <ItemList
            items={matches}
            isLoading={isLoading}
            loadingText="Loading services..."
            emptyState={{
              title: filtered ? "No matching services" : mode === "picker" ? "No active services available" : "No services yet",
              message: filtered ? mode === "picker" ? "Try another name or description." : "Try another name, description or status filter." : mode === "picker" ? "Active services will appear here when they are available." : "Add your first service to get started.",
              action: filtered ? {
                label: mode === "picker" ? "Clear search" : "Clear filters",
                variant: "outline",
                onClick: clearFilters,
              } : mode === "catalogue" && onAddService ? { label: "Add service", onClick: onAddService } : undefined,
            }}
            renderItem={(service) => (
              <ServiceRow
                service={service}
                selected={selectedServices ? selectedServices.some((selected) => selected.id === service.id) : undefined}
                onClick={() => onServiceClick(service)}
                searchText={searchText.trim()}
                showDescription={mode === "picker"}
              />
            )}
          />
        )}
      </Section>
      <p className="sr-only" role="status">{feedback}</p>
      {mode === "catalogue" && businessId && reorderBusinessId === businessId && <ServiceReorder
        key={businessId}
        businessId={businessId}
        services={services}
        onClose={() => setReorderBusinessId(null)}
        onSaved={() => {
          setReorderBusinessId(null);
          setFeedback("Service order saved.");
        }}
      />}
    </div>
  );
}
